import { createApi } from "@reduxjs/toolkit/query/react";
import customBaseQuery from "../../../utils/customBaseQuery";
import { PidApiUserType } from "@lxp-app/pages/event-review/Utils/pidApiUserType";

export const AttendanceApi = createApi({
  reducerPath: "attendanceApi",
  baseQuery: customBaseQuery,
  keepUnusedDataFor: 60,
  tagTypes: ["attendanceApi"],
  endpoints: (builder) => ({
    getAttendanceApi: builder.query({
      query: ({
        attendanceId,
        pageNumber,
        pageSize
      }: {
        attendanceId: string;
        pageNumber?: number;
        pageSize?: number;
      }) => {
        return {
          url: `/attendance/${attendanceId}`,
          method: "GET",
          params: {
            pageNumber: pageNumber,
            pageSize: pageSize
          }
        };
      },
      extraOptions: { service: "Attendance" }
    }),
    initiateAttendanceApi: builder.mutation({
      query: ({ agendaId, data }: any) => ({
        url: `/attendance/${agendaId}`,
        method: "POST",
        body: data
      }),
      extraOptions: { service: "Attendance" },
      invalidatesTags: ["attendanceApi"]
    }),
    updateAttendanceApi: builder.mutation({
      query: ({ attendanceId, data }: any) => ({
        url: `/attendance/mark/${attendanceId}`,
        method: "POST",
        body: data
      }),
      extraOptions: { service: "Attendance" },
      invalidatesTags: ["attendanceApi"]
    }),
    validateAttendanceApi: builder.query({
      query: ({ attendanceId }: any) => ({
        url: `/attendance/validate/${attendanceId}`,
        method: "GET"
      }),
      extraOptions: { service: "Attendance" }
    }),
    attendanceFreezeApi: builder.mutation({
      query: ({ attendanceId, data }: any) => ({
        url: `/attendance/status/${attendanceId}`,
        method: "PUT",
        body: data
      }),
      extraOptions: { service: "Attendance" }
    }),
    forceStopAttendance: builder.mutation({
      query: ({ attendanceId }: any) => ({
        url: `/attendance/${attendanceId}`,
        method: "PUT"
      }),
      extraOptions: { service: "Attendance" }
    }),
    unsubscribeSse: builder.mutation({
      query: (attendanceId) => ({
        url: `/attendance/unsubscribe/${attendanceId}`,
        method: "DELETE"
      }),
      extraOptions: { service: "Attendance" }
    }),
    timeoutApi: builder.mutation({
      query: (attendanceId) => ({
        url: `/attendance/timeout/${attendanceId}`,
        method: "PUT"
      }),
      extraOptions: { service: "Attendance" }
    }),
    downloadTemplate: builder.query({
      query: (agendaId) => ({
        url: `/attendance/download/manual/${agendaId}`,
        method: "GET"
      }),
      extraOptions: { service: "Attendance" }
    }),
    uploadTemplate: builder.mutation({
      query: ({ attendanceId, data }: any) => ({
        url: `/attendance/upload/${attendanceId}`,
        method: "POST",
        body: data
      }),
      extraOptions: { service: "Attendance" }
    }),
    uploadGmeetTemplate: builder.mutation({
      query: ({ attendanceId, data, speakerId }: any) => ({
        url: `/attendance/upload/gmeet/${attendanceId}/${speakerId}`,
        method: "POST",
        body: data
      }),
      extraOptions: { service: "Attendance" }
    }),
    uploadGmeetExternalSpeakerTemplate: builder.mutation({
      query: ({ attendanceId, data, speakerEmail }: any) => ({
        url: `/attendance/upload/gmeet/external-speaker/${attendanceId}/${speakerEmail}`,
        method: "POST",
        body: data
      }),
      extraOptions: { service: "Attendance" }
    }),
    getEnabledUsers: builder.mutation({
      query: ({
        pageNumber,
        pageSize,
        sortKey,
        sortOrder,
        data
      }: PidApiUserType) => ({
        url: `/pid/profile/detail/paginated/enable-ignored`,
        method: "POST",
        params: {
          pageNumber: pageNumber || 1,
          pageSize: pageSize || 6,
          sortKey: sortKey || "createdat",
          sortOrder: sortOrder || "DESC"
        },
        body: data
      }),
      extraOptions: { service: "PID" }
    })
  })
});

export const {
  useLazyGetAttendanceApiQuery,
  useGetAttendanceApiQuery,
  useInitiateAttendanceApiMutation,
  useUpdateAttendanceApiMutation,
  useLazyValidateAttendanceApiQuery,
  useAttendanceFreezeApiMutation,
  useForceStopAttendanceMutation,
  useUnsubscribeSseMutation,
  useTimeoutApiMutation,
  useLazyDownloadTemplateQuery,
  useUploadTemplateMutation,
  useUploadGmeetTemplateMutation,
  useUploadGmeetExternalSpeakerTemplateMutation,
  useGetEnabledUsersMutation
} = AttendanceApi;
